import {
  Box,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Button,
  CloseButton,
  Dialog,
  Grid,
  handleClose,
  ListContainer,
  ListContext,
  ListContextValue,
  newToast,

  PageRoute,
  pn,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useElementBreakpoints,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Select, SubmitButton } from "components";
import {
  LkTermType,
  LkTermTypeId,
  StudentGrade,
  StudentGradeBulkCreate,
  StudentGradeBulkCreateGrade,
  StudentGradeBulkCreateResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { ClassCreateContext, classCreatePageRoute } from "../StudentGradePage/ClassCreatePage/ClassCreatePage";
import { useRouter } from "hooks";

const routeName = "studentGradeBulkCreate";

class Params {
  studentId: number = 0;
}

class OptionalParams {
  schoolYear?: number = 0;
}

export const studentGradeBulkCreatePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "New Grades",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentGradeBulkCreatePage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);

  const listContext = useContext<ListContextValue>(ListContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { matchTemplatePath } = useRouter();

  const [bulkCreate, setBulkCreate] = useState<StudentGradeBulkCreate>({} as StudentGradeBulkCreate);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkCreate] });

  const [resource, setResource] = useState<StudentGradeBulkCreateResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const [showRows, setShowRows] = useState(false);

  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = resource.properties || [];

  useEffect(() => {
    fetch(params.studentId, optionalParams.schoolYear);
    fetchResource();
  }, []);

  useEffect(() => {
    if (bulkCreate?.schoolYear && bulkCreate?.schoolId && bulkCreate?.termTypeId) {
      // try to get classes for the same term first
      ApiService.studentGrades
        .getClassesFromStudent(params.studentId, bulkCreate?.schoolYear, bulkCreate?.termTypeId, bulkCreate?.schoolId)
        .then((response) => {
          const classes = response ?? [];
          const termColumns = getTermColumns(bulkCreate?.termTypeId, resource.termTypes) ?? 0;

          setBulkCreate(
            produce((draft) => {
              draft.rows = [];

              if (classes.length > 0) {
                classes?.forEach((c) => {
                  draft.rows?.push({
                    classId: c.id,
                    class: c,
                    grades: [...Array(termColumns).fill({} as StudentGradeBulkCreateGrade)],
                    summerGrade: {},
                  });
                });
              } else {
                for (let i = 0; i < 6; i++) {
                  draft.rows?.push({
                    classId: undefined,
                    grades: [...Array(termColumns).fill({} as StudentGradeBulkCreateGrade)],
                    summerGrade: {},
                  });
                }
              }

              // draft?.rows?.forEach((row) => {
              //   const currentLength = row.grades?.length ?? 0;

              //   if (currentLength < termColumns) {
              //     // If there are fewer items than needed, add undefined elements to the end
              //     row.grades?.push(...Array(termColumns - currentLength).fill({} as StudentGradeBulkCreateGrade));
              //   } else if (currentLength > termColumns) {
              //     // If there are more items than needed, remove elements from the end
              //     row.grades?.splice(termColumns);
              //   }
              // });
            })
          );
        });

      setShowRows(true);
    } else {
      setShowRows(false);
    }
  }, [bulkCreate?.schoolYear, bulkCreate?.schoolId, bulkCreate?.termTypeId]);

  const fetch = (studentId: number, schoolYear?: number) => {
    ApiService.studentGrades.getBulkCreate(studentId, schoolYear).then((response) => {
      setBulkCreate(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.studentGrades.getBulkCreateResource().then((response) => {
      setResource(response);
    });
  };

  const addRow = () => {
    const termColumns = getTermColumns(bulkCreate?.termTypeId, resource.termTypes);

    setBulkCreate(
      produce((draft) => {
        draft?.rows?.push({
          grades: termColumns ? Array(termColumns).fill({}) : [],
          summerGrade: {},
        });
      })
    );
  };

  const save = (bulkCreate: StudentGradeBulkCreate) => {
    setValidationErrors({});

    ApiService.studentGrades
      .saveBulkCreate(bulkCreate)
      .then((response) => {
        if (response?.badRequest) {
          setBulkCreate(response);
        } else {
          navigate(`../`);
          dispatch(setAppToast(newToast("success")));
          listContext.loadList?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const getTermColumns = (termTypeId: number | undefined, termTypes: LkTermType[] | undefined): number | undefined => {
    const selectedTermType = termTypes?.find((x) => x.id === termTypeId);
    if (!selectedTermType) return undefined;

    if (selectedTermType.id === LkTermTypeId.Summer) return 0;
    if (selectedTermType.termCount) return selectedTermType.termCount;
    if (!selectedTermType.termCount) return 1;
  };

  const addClass = () => {
    navigate({
      pathname: `./${classCreatePageRoute.getPath({ screenType: "dialog" })}`,
      search: classCreatePageRoute.getOptionalParams?.({
        closeOnSave: true,
      }),
    });
  };

  const handleClassCreate = (id: number) => {
  };

  const confirmChange = (): boolean => {
    return !showRows || window.confirm("Changing this value will reset the table. Are you sure?");
  };

  const selectedTermType = resource.termTypes?.find((x) => x.id === bulkCreate?.termTypeId);
  const termColumns = getTermColumns(bulkCreate?.termTypeId, resource.termTypes);

  return (
    <>
      <Dialog
        isForm
        open={true}
        onClose={() => handleClose(navigate, unsavedChanges)}
        maxWidth="lg"
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentGradeBulkCreatePageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              <CloseButton confirm={unsavedChanges} />

              <SubmitButton onClick={() => save(bulkCreate)} disabled={!unsavedChanges} />
            </Stack>
          </Stack>
        }
        content={
          <Stack ref={breakpointsRef} spacing="sm">
            <Grid container spacing="sm">
              <Grid item xs={smallFieldBreakpoints}>
                <Select
                  modelProperty={properties.find((x) => x.propertyName === pn<StudentGradeBulkCreate>("schoolYear"))}
                  errors={validationErrors?.[pn<StudentGradeBulkCreate>("schoolYear")]}
                  value={resource.schoolYears?.find((x) => x.id === bulkCreate?.schoolYear)}
                  options={resource.schoolYears}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    if (confirmChange()) {
                      setBulkCreate(
                        produce((draft) => {
                          draft.schoolYear = value?.id;
                        })
                      );
                    }
                  }}
                />
              </Grid>
              <Grid item xs={smallFieldBreakpoints}>
                <Select
                  modelProperty={properties.find((x) => x.propertyName === pn<StudentGradeBulkCreate>("schoolId"))}
                  errors={validationErrors?.[pn<StudentGradeBulkCreate>("schoolId")]}
                  value={resource.schools?.find((x) => x.id === bulkCreate?.schoolId)}
                  options={resource.schools}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    if (confirmChange()) {
                      setBulkCreate(
                        produce((draft) => {
                          draft.schoolId = value?.id;
                        })
                      );
                    }
                  }}
                />
              </Grid>
              <Grid item xs={smallFieldBreakpoints}>
                <Select
                  modelProperty={properties.find((x) => x.propertyName === pn<StudentGradeBulkCreate>("termTypeId"))}
                  errors={validationErrors?.[pn<StudentGradeBulkCreate>("termTypeId")]}
                  value={resource.termTypes?.find((x) => x.id === bulkCreate?.termTypeId)}
                  options={resource.termTypes}
                  getOptionLabel={(option) => option.name}
                  onChange={(e, value) => {
                    if (confirmChange()) {
                      setBulkCreate(
                        produce((draft) => {
                          draft.termTypeId = value?.id;
                        })
                      );
                    }
                  }}
                />
              </Grid>
            </Grid>

            {showRows && (
              <Stack spacing="sm">
                {/* <pre>{JSON.stringify(bulkCreate, null, 2)}</pre> */}
                <ListContainer
                  type="grid"
                  tableContainer={
                    <TableContainer>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ minWidth: "250px" }}>Class Name</TableCell>
                            {termColumns !== undefined &&
                              termColumns > 0 &&
                              Array.from({ length: termColumns }, (_, index) => (
                                <TableCell key={index}>
                                  {selectedTermType?.name} {termColumns > 1 && index + 1}
                                </TableCell>
                              ))}
                            <TableCell>Summer</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bulkCreate?.rows?.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                              <TableCell>
                                <Select
                                  modelProperty={properties.find((x) => x.propertyName === pn<StudentGrade>("classId"))}
                                  value={row.class}
                                  getOptionLabel={(option) => option.name}
                                  onChange={(e, value) =>
                                    setBulkCreate(
                                      produce((draft) => {
                                        draft.rows![rowIndex].classId = value?.id;
                                        draft.rows![rowIndex].class = value;
                                      })
                                    )
                                  }
                                  isOptionEqualToValue={(option, value) => option.id === value.id}
                                  noOptionsText={<Button onClick={addClass}>+ New Class</Button>}
                                  fetchFunction={(searchString: string) =>
                                    ApiService.search.classes(searchString, bulkCreate.schoolId)
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                      <Stack direction="column">
                                        <Stack direction="row" spacing="xs" alignItems="center">
                                          <Box>{option.name}</Box>
                                          {option.identification && (
                                            <Typography variant="caption">({option.identification})</Typography>
                                          )}
                                        </Stack>
                                        <Typography variant="caption">{option.school?.name}</Typography>
                                      </Stack>
                                    </Box>
                                  )}
                                  size="small"
                                />
                              </TableCell>

                              {row?.grades?.map((grade, gradeColumnIndex) => (
                                <TableCell key={gradeColumnIndex}>
                                  <Select
                                    value={resource.grades?.find((x) => x.id === grade?.gradeId)}
                                    options={resource.grades}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(e, value) =>
                                      setBulkCreate(
                                        produce((draft) => {
                                          draft.rows![rowIndex].grades![gradeColumnIndex].gradeId = value?.id!;
                                        })
                                      )
                                    }
                                    size="small"
                                  />
                                  <Typography variant="caption" color="error">
                                    {grade?.errors}
                                  </Typography>
                                </TableCell>
                              ))}

                              <TableCell>
                                <Select
                                  value={resource.grades?.find((x) => x.id === row.summerGrade?.gradeId)}
                                  options={resource.grades}
                                  getOptionLabel={(option) => option.name}
                                  onChange={(e, value) =>
                                    setBulkCreate(
                                      produce((draft) => {
                                        draft.rows![rowIndex].summerGrade!.gradeId = value?.id!;
                                      })
                                    )
                                  }
                                  size="small"
                                />
                                <Typography variant="caption" color="error">
                                  {row.summerGrade?.errors}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  }
                />

                <Button variant="outlined" onClick={() => addRow()}>
                  + Add Row
                </Button>
              </Stack>
            )}
          </Stack>
        }
      />

      <ClassCreateContext.Provider
        value={{
          onCreate: handleClassCreate,
        }}
      >
        {matchTemplatePath(classCreatePageRoute.templatePath) && <Outlet />}
      </ClassCreateContext.Provider>
    </>
  );
};
